// frontend/app/components/JourneyAssetCompanion.js

import React from "react";
import {
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function JourneyAssetCompanion({
  step,
  asset,
  activeTab = "journey",
  onTabChange,
  onReturnToJourney,
  isDarkMode = false,
  t = (value) => value,
  children,
}) {
  const colors = {
    background: isDarkMode ? "#111827" : "#ffffff",
    panel: isDarkMode ? "#0f172a" : "#f8fafc",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    border: isDarkMode
      ? "rgba(148,163,184,0.24)"
      : "#dbe4ee",
    accent: isDarkMode ? "#38bdf8" : "#0369a1",
    accentSoft: isDarkMode
      ? "rgba(56,189,248,0.12)"
      : "#e0f2fe",
    task: isDarkMode
      ? "rgba(251,191,36,0.10)"
      : "#fffbeb",
    taskBorder: isDarkMode
      ? "rgba(251,191,36,0.25)"
      : "#fde68a",
  };

  const stepTitle =
    step?.title ||
    step?.name ||
    step?.display_name ||
    t("Current Journey Step");

  const stepDescription =
    step?.learning_goal ||
    step?.objective ||
    step?.description ||
    step?.story ||
    "";

  const studentActions = Array.isArray(step?.student_actions)
    ? step.student_actions
    : Array.isArray(step?.actions)
      ? step.actions
      : [];

  const assetType =
    asset?.type === "pwo"
      ? t("3D Exploration")
      : t("Video");

  const assetLabel =
    asset?.label ||
    asset?.sceneName ||
    assetType;

  return (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 16,
        backgroundColor: colors.background,
      }}
    >
      {/* Journey / Saras tabs */}
      <View
        style={{
          flexDirection: "row",
          padding: 8,
          gap: 7,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
          backgroundColor: colors.panel,
        }}
      >
        {[
          { key: "journey", label: t("Journey") },
          { key: "saras", label: t("Saras AI") },
        ].map((tab) => {
          const selected = activeTab === tab.key;

          return (
            <TouchableOpacity
              key={tab.key}
              onPress={() => onTabChange?.(tab.key)}
              activeOpacity={0.75}
              style={{
                flex: 1,
                paddingVertical: 10,
                paddingHorizontal: 10,
                alignItems: "center",
                borderRadius: 10,
                borderWidth: 1,
                borderColor: selected
                  ? colors.accent
                  : colors.border,
                backgroundColor: selected
                  ? colors.accentSoft
                  : colors.background,
              }}
            >
              <Text
                style={{
                  color: selected
                    ? colors.accent
                    : colors.muted,
                  fontSize: 13,
                  fontWeight: "800",
                }}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {activeTab === "saras" ? (
        <View style={{ flex: 1, minHeight: 0 }}>
          {children}
        </View>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            padding: 18,
            paddingBottom: 28,
          }}
          showsVerticalScrollIndicator
        >
          <Text
            style={{
              color: colors.muted,
              fontSize: 12,
              fontWeight: "800",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            {t("Current Step")}
          </Text>

          <Text
            style={{
              color: colors.text,
              fontSize: 21,
              lineHeight: 27,
              fontWeight: "900",
              marginTop: 6,
            }}
          >
            {stepTitle}
          </Text>

          {!!stepDescription && (
            <Text
              style={{
                color: colors.muted,
                fontSize: 14,
                lineHeight: 21,
                marginTop: 10,
              }}
            >
              {stepDescription}
            </Text>
          )}

          <View
            style={{
              marginTop: 18,
              padding: 14,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: colors.panel,
            }}
          >
            <Text
              style={{
                color: colors.muted,
                fontSize: 11,
                fontWeight: "800",
                textTransform: "uppercase",
              }}
            >
              {assetType}
            </Text>

            <Text
              style={{
                color: colors.text,
                fontSize: 15,
                lineHeight: 21,
                fontWeight: "800",
                marginTop: 5,
              }}
            >
              {assetLabel}
            </Text>
          </View>

          {studentActions.length > 0 && (
            <View
              style={{
                marginTop: 18,
                padding: 15,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: colors.taskBorder,
                backgroundColor: colors.task,
              }}
            >
              <Text
                style={{
                  color: colors.text,
                  fontSize: 14,
                  fontWeight: "900",
                  marginBottom: 8,
                }}
              >
                {t("What to do")}
              </Text>

              {studentActions.map((action, index) => {
                const label =
                  typeof action === "string"
                    ? action
                    : action?.text ||
                      action?.prompt ||
                      action?.title ||
                      action?.description ||
                      `${t("Task")} ${index + 1}`;

                return (
                  <Text
                    key={action?.action_id || action?.id || index}
                    style={{
                      color: colors.text,
                      fontSize: 14,
                      lineHeight: 21,
                      marginTop: index === 0 ? 0 : 7,
                    }}
                  >
                    {"•"} {label}
                  </Text>
                );
              })}
            </View>
          )}

          <TouchableOpacity
            onPress={onReturnToJourney}
            activeOpacity={0.8}
            style={{
              marginTop: 22,
              paddingVertical: 12,
              paddingHorizontal: 15,
              borderRadius: 11,
              alignItems: "center",
              backgroundColor: colors.accent,
            }}
          >
            <Text
              style={{
                color: "#ffffff",
                fontSize: 14,
                fontWeight: "900",
              }}
            >
              {t("Return to Full Journey")}
            </Text>
          </TouchableOpacity>
        </ScrollView>
      )}
    </View>
  );
}